import React, { useCallback, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Task } from "@common/types/task";
import { FinancialDocumentViewSchema } from "@common/types/views/financialDocumentViews";
import { financialDocumentQueryKeys } from "@components/documents/lib";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { StickyTaskMembershipPending } from "../shared/StickyTaskMembershipPending";
import { useStickyRefreshOnMount } from "../shared/useStickyRefreshOnMount";
import { useTaskTableViewState } from "../shared/useTaskTableViewState";
import { FinancialDocumentsTaskTable } from "./FinancialDocumentsTaskTable";
import { getFinancialDocumentsTaskFilterTabs } from "./financialDocumentsTaskFilters";
import { parseFinancialDocumentsTaskView } from "./financialDocumentsTaskView";
import styles from "./FinancialDocuments.module.scss";

interface Props {
    selectedTask: Task;
}

export const FinancialDocumentsTaskBody: React.FC<Props> = ({
    selectedTask,
}) => {
    const queryClient = useQueryClient();
    const {
        taskView,
        sizedView,
        allowedColumnIds,
        handleViewChange,
        handleFiltersChange,
    } = useTaskTableViewState({
        selectedTask,
        viewSchema: FinancialDocumentViewSchema,
        parseView: parseFinancialDocumentsTaskView,
    });
    const originalFilters = selectedTask.view?.originalFilters ?? undefined;

    const invalidateList = useCallback(() => {
        void queryClient.invalidateQueries({
            queryKey: financialDocumentQueryKeys.lists(),
        });
    }, [queryClient]);

    const { isMembershipPending, resolvedMembership } = useStickyRefreshOnMount(
        { selectedTask, onMembershipAdded: invalidateList },
    );
    const stickyDocumentIds =
        resolvedMembership?.financialDocumentIds ??
        selectedTask.view?.filters?.financialDocumentIds;

    // A sticky task or a selection owns its membership; only free tasks edit it.
    const isMembershipFixed =
        !!originalFilters || !!taskView.filters.financialDocumentIds?.length;
    const availableFilters = useMemo(
        () =>
            isMembershipFixed
                ? []
                : getFinancialDocumentsTaskFilterTabs({
                      isWorkspaceScoped: true,
                  }),
        [isMembershipFixed],
    );

    if (isMembershipPending) {
        return <StickyTaskMembershipPending />;
    }

    return (
        <div className={styles.root}>
            <FinancialDocumentsTaskTable
                view={sizedView}
                onViewChange={handleViewChange}
                onFiltersChange={handleFiltersChange}
                availableFilters={availableFilters}
                defaultView={taskView}
                allowedColumnIds={allowedColumnIds}
                originalFilters={originalFilters}
                stickyDocumentIds={stickyDocumentIds}
                className={styles.filters}
                paginationType="loadMore"
                emptyView={() => (
                    <EmptyState
                        header={
                            isMembershipFixed
                                ? "No documents associated with this task"
                                : "No documents matching the task definition"
                        }
                    />
                )}
            />
        </div>
    );
};
